import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  UserCredential,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import {
  User,
  UserRole,
  RegisterFormData,
  UserPreferences,
  AuthError,
  AuthErrorCode
} from '../types/auth';
import { ChangeTrackingService } from './changeTrackingService';
import { NotificationService } from './notificationService';

/**
 * Authentication Service
 * Handles all authentication-related operations using Firebase Auth and Firestore
 */
export class AuthService {
  /**
   * Register a new user with email and password
   */
  static async register(formData: RegisterFormData): Promise<User> {
    try {
      // Validate family code if required
      if (process.env.REACT_APP_REQUIRE_FAMILY_CODE === 'true') {
        const expectedCode = process.env.REACT_APP_FAMILY_REGISTRATION_CODE;
        if (!formData.familyCode || formData.familyCode !== expectedCode) {
          throw new Error('Invalid family registration code');
        }
      }

      // Create Firebase Auth user
      const userCredential: UserCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      const firebaseUser = userCredential.user;

      // Update Firebase Auth profile
      await updateProfile(firebaseUser, {
        displayName: formData.displayName,
      });

      // Create default user preferences
      const defaultPreferences: UserPreferences = {
        emailNotifications: true,
        pushNotifications: false,
        theme: 'auto',
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        privacy: {
          showEmail: false,
          showPhone: false,
          allowFamilyContact: true,
        },
      };

      // Create user document in Firestore
      const userData: any = {
        id: firebaseUser.uid,
        uid: firebaseUser.uid,
        email: formData.email,
        displayName: formData.displayName,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: 'viewer' as UserRole, // Default role
        isApproved: process.env.REACT_APP_ENABLE_ADMIN_APPROVAL !== 'true', // Auto-approve if admin approval disabled
        profileComplete: false,
        isEmailVerified: false,
        preferences: defaultPreferences,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: firebaseUser.uid,
      };

      await setDoc(doc(db, 'users', firebaseUser.uid), userData);

      // Send email verification if enabled
      if (process.env.REACT_APP_ENABLE_EMAIL_VERIFICATION === 'true') {
        await sendEmailVerification(firebaseUser);
      }

      // Send welcome notification (non-blocking)
      NotificationService.createNotification({
        userId: firebaseUser.uid,
        type: 'welcome',
      }).catch(err => console.error('Failed to create welcome notification:', err));

      return userData;
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign in with email and password
   */
  static async signIn(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Get user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (!userDoc.exists()) {
        throw new Error('User data not found');
      }

      const userData = userDoc.data() as User;

      // Check if user is approved
      if (!userData.isApproved) {
        await signOut(auth);
        const error: AuthError = {
          code: 'custom/user-not-approved',
          message: 'Your account is pending admin approval.',
        };
        throw error;
      }

      // Update last login time
      await updateDoc(doc(db, 'users', firebaseUser.uid), {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        ...userData,
        isEmailVerified: firebaseUser.emailVerified,
      };
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign out current user
   */
  static async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Send password reset email
   */
  static async sendPasswordReset(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Send email verification
   */
  static async sendEmailVerification(): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user');
      }
      await sendEmailVerification(user);
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Update user profile
   */
  static async updateUserProfile(
    userId: string,
    updates: Partial<User>
  ): Promise<void> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || firebaseUser.uid !== userId) {
        throw new Error('Unauthorized');
      }

      // Get current state before updating
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      const beforeState = userDoc.exists() ? { ...userDoc.data(), id: userId } : null;

      // Update Firebase Auth profile if display name changed
      if (updates.displayName && updates.displayName !== firebaseUser.displayName) {
        await updateProfile(firebaseUser, {
          displayName: updates.displayName,
        });
      }

      // Update Firestore document
      const userUpdates = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(userRef, userUpdates);

      // Get updated state
      const updatedDoc = await getDoc(userRef);
      const afterState = updatedDoc.exists() ? { ...updatedDoc.data(), id: userId } : null;

      // Log change tracking
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'user',
            entityId: userId,
            changeType: 'update',
            beforeState: beforeState,
            afterState: afterState,
            changedFields: Object.keys(updates),
            reason: 'User profile updated',
          },
          userId,
          firebaseUser.displayName || 'Unknown User',
          false // User updating their own profile
        );
      } catch (changeError) {
        console.error('Error logging user profile update change:', changeError);
      }
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Change user password
   */
  static async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) {
        throw new Error('No authenticated user');
      }

      // Re-authenticate user
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update password
      await updatePassword(user, newPassword);

      // Update last modified time in Firestore
      await updateDoc(doc(db, 'users', user.uid), {
        updatedAt: serverTimestamp(),
      });
    } catch (error: any) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * Get current user data from Firestore
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        return null;
      }

      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data() as User;
      return {
        ...userData,
        isEmailVerified: firebaseUser.emailVerified,
      };
    } catch (error: any) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Check if user has required role
   */
  static hasRole(user: User | null, requiredRole: UserRole): boolean {
    if (!user) return false;

    const roleHierarchy: Record<UserRole, number> = {
      viewer: 1,
      contributor: 2,
      editor: 3,
      admin: 4,
    };

    return roleHierarchy[user.role] >= roleHierarchy[requiredRole];
  }

  /**
   * Check if user can perform action
   */
  static canPerformAction(user: User | null, action: string): boolean {
    if (!user || !user.isApproved) return false;

    const permissions: Record<UserRole, string[]> = {
      viewer: ['read'],
      contributor: ['read', 'comment', 'suggest'],
      editor: ['read', 'comment', 'suggest', 'create', 'edit'],
      admin: ['read', 'comment', 'suggest', 'create', 'edit', 'delete', 'approve', 'manage'],
    };

    return permissions[user.role]?.includes(action) || false;
  }

  /**
   * Handle authentication errors and convert to standardized format
   */
  private static handleAuthError(error: any): AuthError {
    const errorCode = error.code as AuthErrorCode;
    
    const errorMessages: Record<string, string> = {
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/invalid-credential': 'Invalid email or password. Please try again.',
      'auth/user-disabled': 'This account has been disabled.',
      'auth/user-not-found': 'Invalid email or password. Please try again.',
      'auth/wrong-password': 'Invalid email or password. Please try again.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 6 characters.',
      'auth/network-request-failed': 'Network error. Please check your connection.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/user-token-expired': 'Your session has expired. Please sign in again.',
      'custom/insufficient-permissions': 'You do not have permission to perform this action.',
      'custom/user-not-approved': 'Your account is pending admin approval.',
      'custom/profile-incomplete': 'Please complete your profile before continuing.',
    };

    return {
      code: errorCode || 'unknown',
      message: errorMessages[errorCode] || error.message || 'An unexpected error occurred.',
      details: error.message,
    };
  }
}

export default AuthService;